// The eye controller: it owns the eyeballs and everything that moves them.

import { quaternion, vector3 } from '../../math/index.ts'
import type { Quaternion, Vector3 } from '../../math/index.ts'
import { EYE_SIDES } from '../eyeSide.ts'
import type { EyeSide } from '../eyeSide.ts'
import { createGazeController } from './gazeController.ts'
import type { GazeController, GazeControllerOptions } from './gazeController.ts'

/** One eyeball, as the controller needs it — the subset of the GLB's mesh part. */
export type EyeShell = {
  localVertices: Float32Array
  pivot: Vector3
  opticalAxis: Vector3
  poleIndex: number
}

/** Sphere frame of the posed shell: the lift scales its extents by this radius. */
export type EyeShellFrame = {
  centre: Vector3
  radius: number
  axis: Vector3
}

export type EyePose = {
  targetDirection: Vector3
  rotation: Quaternion
  /** Posed world metres, reused across passes — copy it if you need to keep it. */
  vertices: Float32Array
  frame: EyeShellFrame
  /** How far the pole vertex travelled: the number that says the eye actually moved. */
  poleTravelMm: number
  /** dot(posed axis, requested direction) — 1.0 when the eye is on target. */
  aim: number
}

export type EyeController = ReturnType<typeof createEyeController>

export function createEyeController(shells: Record<EyeSide, EyeShell>,
                                    options: GazeControllerOptions = {}) {
  const gaze: GazeController = createGazeController(options)
  const rest = { left: restState(shells.left), right: restState(shells.right) }
  const poses = { left: initialPose(rest.left), right: initialPose(rest.right) }

  function updateSide(side: EyeSide): EyePose {
    const shell = rest[side]
    const pose = poses[side]
    const target = gaze.resolve(side, shell.opticalAxis, shell.pivot)
    const rotation = quaternion.fromUnitVectors(shell.opticalAxis, target)
    quaternion.rotateVertices(shell.localVertices, shell.pivot, rotation, pose.vertices)
    pose.targetDirection = target
    pose.rotation = rotation
    pose.frame = poseFrame(shell, rotation)
    pose.poleTravelMm = poleTravelMm(shell, rotation)
    pose.aim = vector3.dot(quaternion.rotate(rotation, shell.opticalAxis), target)
    return pose
  }

  return {
    gaze,

    /** Recompute both eyes from the current gaze request. */
    update(): Record<EyeSide, EyePose> {
      for (const side of EYE_SIDES) updateSide(side)
      return poses
    },

    /** Recompute and return one eye. */
    updateSide,

    /** The last computed pose, without recomputing. */
    pose(side: EyeSide): EyePose { return poses[side] },

    restAxis(side: EyeSide): Vector3 { return rest[side].opticalAxis },

    /** Between the two pivots — where a light set is sampled for the pair. */
    midpoint(): Vector3 { return vector3.midpoint(rest.left.pivot, rest.right.pivot) },
  }
}

type RestState = EyeShell & {
  meanLocal: Vector3
  meanRadius: number
  poleAxisLocal: Vector3
}

// The reduction over the shell, once at construction rather than once per pass.
function restState(shell: EyeShell): RestState {
  const vertices = shell.localVertices
  const count = vertices.length / 3
  let sumX = 0, sumY = 0, sumZ = 0
  for (let vertex = 0; vertex < count; vertex++) {
    sumX += vertices[vertex * 3]
    sumY += vertices[vertex * 3 + 1]
    sumZ += vertices[vertex * 3 + 2]
  }
  const meanLocal: Vector3 = [sumX / count, sumY / count, sumZ / count]
  let radiusSum = 0
  for (let vertex = 0; vertex < count; vertex++) {
    radiusSum += Math.hypot(vertices[vertex * 3] - meanLocal[0],
      vertices[vertex * 3 + 1] - meanLocal[1],
      vertices[vertex * 3 + 2] - meanLocal[2])
  }
  const pole = vector3.fromArray(vertices, shell.poleIndex)
  return {
    ...shell,
    opticalAxis: vector3.normalize(shell.opticalAxis),
    meanLocal,
    meanRadius: radiusSum / count,
    poleAxisLocal: vector3.normalize(vector3.subtract(pole, meanLocal)),
  }
}

function poseFrame(shell: RestState, rotation: Quaternion): EyeShellFrame {
  return {
    centre: vector3.add(shell.pivot, quaternion.rotate(rotation, shell.meanLocal)),
    radius: shell.meanRadius,
    axis: quaternion.rotate(rotation, shell.poleAxisLocal),
  }
}

function poleTravelMm(shell: RestState, rotation: Quaternion): number {
  const pole = vector3.fromArray(shell.localVertices, shell.poleIndex)
  return vector3.distance(quaternion.rotate(rotation, pole), pole) * 1e3
}

function initialPose(shell: RestState): EyePose {
  return {
    targetDirection: shell.opticalAxis,
    rotation: quaternion.IDENTITY,
    vertices: new Float32Array(shell.localVertices.length),
    frame: poseFrame(shell, quaternion.IDENTITY),
    poleTravelMm: 0,
    aim: 1,
  }
}
